import React, { useEffect } from "react";
import styles from "./FavoritesPage.module.scss";
import SectionHeader from "@src/components/widgets/SectionHeader/SectionHeader";
import {
  Dropdown,
  PaginationControls,
  GridContainer,
} from "@swordsweeper/ssi-ui-component-library";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import PxProductTile from "@src/components/widgets/PxProductTile";
import { useFavoriteProductsStore } from "@src/state/favoriteProductsState";

const SORT_BY_ALPHABETICAL = "name_asc";
const SORT_BY_ALPHABETICAL_DESC = "name_desc";
const SORT_BY_LABELS = {
  [SORT_BY_ALPHABETICAL]: "A → Z",
  [SORT_BY_ALPHABETICAL_DESC]: "Z → A",
} as const;

type SortByOption =
  | typeof SORT_BY_ALPHABETICAL
  | typeof SORT_BY_ALPHABETICAL_DESC
  | null;

function FavoritesPage() {
  const { favorites, loading, pagination, fetchFavorites, setPagination } =
    useFavoriteProductsStore();

  useEffect(() => {
    fetchFavorites({ page: 1 });
  }, []);

  const handlePaginationFilterChanged = ({
    name,
    value,
  }: {
    name: string;
    value: number;
  }) => {
    const updatedPagination = {
      ...pagination,
      [name]: value,
    };
    if (name === "pageSize") {
      updatedPagination.page = 1;
    }
    setPagination(updatedPagination);
    fetchFavorites(updatedPagination);
  };

  const products = (favorites ?? []).map((fav) => fav.product);
  const isEmpty = !loading && products.length === 0;

  return (
    <div>
      {loading && <SpinningLoader />}
      <SectionHeader
        header={<span className={styles.pageTitle}>Favorite Products</span>}
        rightSideContent={
          <Dropdown
            className="dropdown"
            items={[
              { label: "Default", value: null },
              {
                label: SORT_BY_LABELS[SORT_BY_ALPHABETICAL],
                value: SORT_BY_ALPHABETICAL,
              },
              {
                label: SORT_BY_LABELS[SORT_BY_ALPHABETICAL_DESC],
                value: SORT_BY_ALPHABETICAL_DESC,
              },
            ]}
            label="Sort by"
            onItemClick={() => {}}
            size={150}
            dropdownIcon={<i className="fa-sharp fa-regular fa-chevron-down" />}
          />
        }
      />
      <div className="padding-top-xxl">
        {isEmpty ? (
          <div className={styles.emptyState}>
            <p>You have no favorite products yet.</p>
            <p>
              Click the heart icon on any product to add it to your favorites.
            </p>
          </div>
        ) : (
          <>
            <GridContainer columns={{ base: 5 }} gap="var(--margin-sm)">
              {products.map((product, index) => (
                <div key={`favorites-page-product-${product.id ?? index}`}>
                  <PxProductTile
                    product={product}
                    hideFavoriteHeart
                    className="width-100-important"
                  />
                </div>
              ))}
            </GridContainer>
            <div className="padding-top-xxl">
              <PaginationControls
                onChange={handlePaginationFilterChanged}
                values={pagination}
                data-testid="favorites-page-pagination-controls"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default FavoritesPage;
